import React from "react";

/**
 * Change on the previous period, set in body ink with an arrow and a sign
 * (DESIGN.md, Neutral Change Rule). `null` means the previous period had no
 * activity at all, so there is no baseline to compare against.
 */
export const Change = ({
  value,
  comparable,
}: {
  value: number | null | undefined;
  comparable: boolean;
}) => {
  if (!comparable) {
    return (
      <span className="text-ink-muted fs-12">No earlier period to compare</span>
    );
  }
  if (value === null || value === undefined) {
    return <span className="text-ink-muted fs-12">New this period</span>;
  }

  const flat = value === 0;
  const up = value > 0;
  const icon = flat
    ? "ri-subtract-line"
    : up
    ? "ri-arrow-right-up-line"
    : "ri-arrow-right-down-line";

  return (
    <span className="text-body fs-12">
      <i className={`${icon} align-middle me-1`} aria-hidden="true"></i>
      {flat ? "No change" : `${up ? "+" : "−"}${Math.abs(value)}%`}
      <span className="text-ink-muted"> vs. previous period</span>
    </span>
  );
};

export interface Figure {
  label: string;
  value: number;
  decimals?: number;
  /** Omit to show the figure without a comparison line. */
  change?: number | null;
}

/** One number with its label; the number is not a heading. */
export const FigureCell = ({
  figure,
  comparable = false,
  loading = false,
  size = "md",
}: {
  figure: Figure;
  comparable?: boolean;
  loading?: boolean;
  size?: "md" | "sm";
}) => (
  <div className={`figure-row__cell figure-row__cell--${size}`}>
    <p className="figure-row__label">{figure.label}</p>
    {loading ? (
      <span className="placeholder-glow d-block">
        <span className="placeholder col-6"></span>
      </span>
    ) : (
      <p className="figure-row__value">
        {figure.value.toLocaleString(undefined, {
          minimumFractionDigits: figure.decimals || 0,
          maximumFractionDigits: figure.decimals || 0,
        })}
      </p>
    )}
    {figure.change !== undefined && !loading ? (
      <Change value={figure.change} comparable={comparable} />
    ) : null}
  </div>
);

/**
 * A row of figures separated by hairlines. It wraps onto as many lines as the
 * width needs, and the hairlines follow because they are the grid's gap.
 */
const FigureRow = ({
  figures,
  comparable,
  loading,
  size,
  children,
}: {
  figures?: Figure[];
  comparable?: boolean;
  loading?: boolean;
  size?: "md" | "sm";
  children?: React.ReactNode;
}) => (
  <div className={`figure-row figure-row--${size || "md"}`}>
    {(figures || []).map((figure) => (
      <FigureCell
        key={figure.label}
        figure={figure}
        comparable={comparable}
        loading={loading}
        size={size}
      />
    ))}
    {children}
  </div>
);

export default FigureRow;
